import React, { useState } from "react";
import { MapPin, Mail, Phone, Clock, Send } from "lucide-react";
import { SERVICES, COMPANY } from "../mock";
import { PageHero, Reveal } from "../components/shared";
import { useToast } from "../hooks/use-toast";
import { submitQuote } from "../api";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Please complete name, email, and project details." });
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote({ ...form, source: "contact" });
      toast({ title: "Request submitted", description: "Thanks for reaching out. Our team will respond shortly." });
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or email us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const label = "block text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 mb-2";
  const inputCls =
    "w-full rounded-xl bg-white border border-white px-4 py-3.5 text-[15px] text-[#0b2545] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/60 transition-all";

  const infoItems = [
    { icon: Mail, title: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, title: "Office Address", value: COMPANY.location },
    { icon: Phone, title: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
  ];

  return (
    <>
      <PageHero
        crumb="Contact"
        eyebrow="Contact Page"
        title="Get in Touch"
        subtitle="Have a project in mind? Need technical support? Our team is here to help."
      />

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            {/* Contact information */}
            <Reveal className="lg:col-span-2">
              <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-slate-500 mb-8">
                Contact Information
              </p>
              <div className="space-y-1">
                {infoItems.map((it, i) => (
                  <div key={i} className="flex items-start gap-4 py-6 border-t border-black/10">
                    <it.icon className="w-5 h-5 text-[#1e73ff] mt-1 shrink-0" />
                    <div>
                      <h3 className="font-display font-bold text-[17px] text-[#0b2545]">{it.title}</h3>
                      {it.href ? (
                        <a href={it.href} className="mt-1 inline-block text-[15px] text-slate-600 hover:text-[#14b8a6] transition-colors">
                          {it.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-[15px] text-slate-600">{it.value}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex items-start gap-4 py-6 border-t border-black/10">
                  <Clock className="w-5 h-5 text-[#1e73ff] mt-1 shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-[17px] text-[#0b2545] mb-3">Business Hours</h3>
                    <ul className="space-y-2">
                      {COMPANY.hours.map((h, i) => (
                        <li key={i} className="flex items-center justify-between gap-6 text-[14px]">
                          <span className="text-slate-600">{h.day}</span>
                          <span className="text-[#0b2545] font-medium">{h.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Form */}
            <Reveal delay={120} className="lg:col-span-3">
              <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-slate-500 mb-3">
                Request a Quote
              </p>
              <h2 className="font-display font-extrabold text-[#0b2545] text-[32px] lg:text-[40px] hero-heading mb-8">
                Tell us about your project
              </h2>
              <div className="bg-[#0b2545] rounded-3xl shadow-sm p-7 lg:p-9">
                <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={label}>Name</label>
                    <input data-testid="contact-name-input" className={inputCls} placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <label className={label}>Company</label>
                    <input data-testid="contact-company-input" className={inputCls} placeholder="Company name (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  </div>
                  <div>
                    <label className={label}>Email</label>
                    <input type="email" data-testid="contact-email-input" className={inputCls} placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <label className={label}>Phone Number</label>
                    <input data-testid="contact-phone-input" className={inputCls} placeholder="Optional" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label}>Service Required</label>
                    <select data-testid="contact-service-select" className={inputCls} value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label}>Project Details</label>
                    <textarea rows={5} data-testid="contact-message-input" className={`${inputCls} resize-none`} placeholder="Tell us about the site, the scope, and any deadlines." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" data-testid="contact-submit-button" disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[15px] font-semibold px-8 py-4 hover:bg-[#155ed6] transition-colors disabled:opacity-60">
                      {submitting ? "Submitting..." : "Submit Request"} <Send className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
